'use client';

import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import Image from 'next/image';
import { PiMusicNotesSimpleThin } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { Sample } from '@/sharedTypes/Api';
import { rightLanguage } from '@/sharedTypes/helpers';
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import { useRouter, useSearchParams } from 'next/navigation';
import SamplesFinder from '../Samples/SamplesSelector/SamplesFinder';
import Pagination from '@/app/posts/components/Pagination';
import { SearchInput } from '@/components/ui/search-input';
import { TypographyH2, TypographyMuted } from '../ui/typography';
import { NEXT_PUBLIC_SAMPLES_CDN_URL } from '@/shared/envs';
import { Skeleton } from '../ui/skeleton';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';

type SamplesGridProps = {
  category?: string;
  limit?: number;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
};

const SampleCard = observer(({ sample }: { sample: Sample }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const lang = useLanguageFromPath();
  const title = rightLanguage(sample.metadata.title) || 'Untitled';
  const author = rightLanguage(sample.metadata.author) || 'Unknown';
  const previewImageUrl = sample.webpPath
    ? `${NEXT_PUBLIC_SAMPLES_CDN_URL || 'https://cdn.darpiano.com'}/${sample.webpPath}`
    : undefined;

  const handleClick = () => {
    // Preserve all existing URL params (including page number)
    const params = new URLSearchParams(searchParams.toString());
    params.set('sheet-music', sample.identifier);

    // Preserve the current language path
    const basePath = lang === 'ru' ? '/ru' : '/';
    router.push(`${basePath}?${params.toString()}`, { scroll: false });
  };

  return (
    <div onClick={handleClick} className='cursor-pointer group overflow-hidden'>
      {/* Preview Image */}
      <div className='aspect-[25/33] rounded-md border overflow-hidden w-full bg-white'>
        <div className='relative w-full h-full hover:zoom-in-50 transition-all duration-200'>
          {previewImageUrl ? (
            <Image
              src={previewImageUrl}
              alt={`${title} music sheet preview`}
              fill
              className='object-contain object-top p-2'
              sizes='(max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw'
              unoptimized
            />
          ) : (
            <div className='flex items-center justify-center w-full h-full text-zinc-400'>
              {/* <PiMusicNotesSimpleFill size={32} /> */}
              <PiMusicNotesSimpleThin size={32} />
            </div>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className='pt-2'>
        <h3 className='text-base text-accent-blue-600 font-medium capitalize'>
          {author}
        </h3>
        <p className='text-sm text-zinc-500 capitalize mt-0.5'>{title}</p>
      </div>
    </div>
  );
});

const SamplesGrid = observer(
  ({ category, limit, searchQuery, onSearchChange }: SamplesGridProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const searchParams = useSearchParams();
    const lang = useLanguageFromPath();
    const [isLoading, setIsLoading] = useState(true);
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Read initial page from URL params, default to 1
    const pageFromUrl = searchParams.get('page');
    const initialPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
    const [currentPage, setCurrentPage] = useState(initialPage);
    const ITEMS_PER_PAGE = 20;

    useEffect(() => {
      const loadSamples = async () => {
        if (samplesSelectorModel.samples.length === 0) {
          await samplesSelectorModel.refreshSamples();
        }
        setIsLoading(false);
      };

      loadSamples();
    }, []);

    // Prevent auto-focus on mobile devices
    useEffect(() => {
      const isMobileDevice =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobile = isMobileDevice || isTouchDevice;

      if (isMobile && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.blur();
        }, 0);
      }
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchQuery(value);
      onSearchChange?.(value);
    };

    // Sync state with URL params when they change
    useEffect(() => {
      const pageFromUrl = searchParams.get('page');
      const urlPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
      if (urlPage !== currentPage) {
        setCurrentPage(urlPage);
      }
    }, [searchParams, currentPage]);

    // Reset to page 1 when search or category changes (but not when page param changes)
    const prevSearchQuery = React.useRef(searchQuery);
    const prevCategory = React.useRef(category);

    useEffect(() => {
      const searchChanged = prevSearchQuery.current !== searchQuery;
      const categoryChanged = prevCategory.current !== category;

      if (!limit && (searchChanged || categoryChanged)) {
        prevSearchQuery.current = searchQuery;
        prevCategory.current = category;

        setCurrentPage(1);
        // Update URL to page 1
        const params = new URLSearchParams(searchParams.toString());
        params.delete('page'); // Remove page param when on page 1
        const basePath = lang === 'ru' ? '/ru' : '';
        router.push(`${basePath}?${params.toString()}`, { scroll: false });
      }
    }, [searchQuery, category, limit, searchParams, router, lang]);

    // Handle edge case where current page exceeds total pages after filtering
    useEffect(() => {
      if (!limit && samplesSelectorModel.samples.length > 0) {
        // Apply search filter first if search query exists
        let filteredSamples = samplesSelectorModel.samples;

        if (searchQuery && searchQuery.length > 0) {
          const samplesFinder = new SamplesFinder(samplesSelectorModel.samples);
          filteredSamples = samplesFinder.search(searchQuery);
        }

        // Then apply category filter if specified
        if (category) {
          filteredSamples = filteredSamples.filter(
            (s) =>
              rightLanguage(s.category)?.toLowerCase() ===
              category.toLowerCase()
          );
        }

        const totalPagesCalc = Math.ceil(
          filteredSamples.length / ITEMS_PER_PAGE
        );
        if (currentPage > totalPagesCalc && totalPagesCalc > 0) {
          setCurrentPage(totalPagesCalc);
          const params = new URLSearchParams(searchParams.toString());
          if (totalPagesCalc === 1) {
            params.delete('page');
          } else {
            params.set('page', totalPagesCalc.toString());
          }
          const basePath = lang === 'ru' ? '/ru' : '';
          router.push(`${basePath}?${params.toString()}`, { scroll: false });
        }
      }
    }, [
      samplesSelectorModel.samples.length,
      currentPage,
      limit,
      searchQuery,
      category,
      searchParams,
      router,
      lang,
    ]);

    // Apply search filter first if search query exists
    let samples = samplesSelectorModel.samples;

    if (searchQuery && searchQuery.length > 0) {
      const samplesFinder = new SamplesFinder(samplesSelectorModel.samples);
      samples = samplesFinder.search(searchQuery);
    }

    // Then apply category filter if specified
    if (category) {
      samples = samples.filter(
        (s) =>
          rightLanguage(s.category)?.toLowerCase() === category.toLowerCase()
      );
    }

    // Calculate pagination values
    const totalPages = Math.ceil(samples.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    // Slice samples for current page (or apply limit if specified)
    const displayedSamples = limit
      ? samples.slice(0, limit)
      : samples.slice(startIndex, endIndex);

    // Handler for page changes with scroll-to-top and URL update
    const handlePageChange = (page: number) => {
      setCurrentPage(page);

      // Update URL with new page number
      const params = new URLSearchParams(searchParams.toString());
      if (page === 1) {
        params.delete('page'); // Clean URL for page 1
      } else {
        params.set('page', page.toString());
      }
      const basePath = lang === 'ru' ? '/ru' : '';
      router.push(`${basePath}?${params.toString()}`, { scroll: false });

      // Smooth scroll to top of page
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };

    if (isLoading) {
      return (
        <>
          <div>
            <TypographyH2>{t('mainPage.samplesGrid.title')}</TypographyH2>
            <TypographyMuted>
              {t('mainPage.samplesGrid.subtitle')}
            </TypographyMuted>
          </div>

          <div className='pb-8 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-8'>
            {[...Array(10)].map((_, i) => (
              <div key={i} className='space-y-2'>
                <Skeleton className='aspect-[25/33] rounded-md' />
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-3/4' />
                  <Skeleton className='h-3 w-1/2' />
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (samples.length === 0) {
      return (
        <div className='text-center py-12 text-zinc-500'>
          {t('mainPage.samplesGrid.noResults')}
        </div>
      );
    }

    return (
      <>
        <div>
          <TypographyH2>{t('mainPage.samplesGrid.title')}</TypographyH2>
          <TypographyMuted>
            {t('mainPage.samplesGrid.subtitle')}
          </TypographyMuted>
        </div>
        {/* Mobile Search & Filters - only visible on mobile/tablet */}
        <div className='lg:hidden mb-6 flex flex-col gap-3'>
          <SearchInput
            ref={inputRef}
            value={localSearchQuery}
            onChange={handleSearchChange}
            placeholder={t('mainPage.samplesGrid.searchPlaceholder')}
            testId='mobile_search_input'
          />
          {/* <MobileFilters /> */}
        </div>

        {/* Samples grid */}
        <div className='pb-8 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-8'>
          {displayedSamples.map((sample) => (
            <div key={sample.identifier} className='w-full sm:max-w-[180px]'>
              <SampleCard sample={sample} />
            </div>
          ))}
        </div>

        {/* Pagination component */}
        {!limit && displayedSamples.length > 0 && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalPosts={samples.length}
            hasNextPage={currentPage < totalPages}
            hasPreviousPage={currentPage > 1}
            onPageChange={handlePageChange}
            labels={{
              previous: t('mainPage.pagination.previous'),
              next: t('mainPage.pagination.next'),
              goToPage: t('mainPage.pagination.goToPage'),
              of: t('mainPage.pagination.of'),
            }}
          />
        )}
      </>
    );
  }
);

export default SamplesGrid;
